import { useQuery } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorFeature } from '@/types/core/connector';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/**
 * Hook to find a specific connector feature by connector id and provider feature id.
 */
export function useFindConnectorFeature(
  tenantId?: UUID,
  connectorId?: UUID,
  featureId?: UUID
) {
  return useQuery<ConnectorFeature | null, Error>({
    queryKey: connectorQueryKeys.featureDetail(
      tenantId,
      connectorId,
      featureId
    ),
    queryFn: async () => {
      if (isEmptyUUID(tenantId)) throw new Error('Missing tenant id');
      if (isEmptyUUID(connectorId)) throw new Error('Missing connector id');
      if (isEmptyUUID(featureId)) throw new Error('Missing feature id');
      const result = await connectorService.findConnectorFeature(
        tenantId!,
        connectorId!,
        featureId!
      );
      if (!result) throw new Error('Feature not available yet');
      return result;
    },
    enabled:
      !isEmptyUUID(tenantId) &&
      !isEmptyUUID(connectorId) &&
      !isEmptyUUID(featureId),
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
}
